import { useState, useRef, useEffect, type FormEvent } from 'react'
import {
  X,
  Send,
  RotateCcw,
  Sparkles,
  ChevronDown,
  ChevronUp,
  UserPlus,
  Search,
  Calendar,
  MessageSquare,
  Loader2,
  AlertCircle,
  SquarePen,
  Bot,
  User,
  Wrench,
} from 'lucide-react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { cn } from '@/lib/utils'
import { useChat, type ChatMessage, type ToolCallEvent } from '@/hooks/use-chat'
import { VoiceChatInput } from '@/components/VoiceChatInput'
import { ChatArtifact } from '@/components/ChatArtifact'

interface ChatPanelProps {
  open: boolean
  onClose: () => void
  voiceStart?: boolean
  /** Pre-fill the chat input with this text (e.g. from voice overlay transcription) */
  initialInput?: string | null
}

const SUGGESTIONS = [
  { label: 'Log a meeting', icon: MessageSquare },
  { label: 'Add a new contact', icon: UserPlus },
  { label: 'Set a reminder', icon: Calendar },
  { label: "Who haven't I contacted recently?", icon: Search },
]

const TOOL_ICONS: Record<string, typeof Wrench> = {
  search_contacts: Search,
  create_contact: UserPlus,
  import_contact_from_linkedin: UserPlus,
  update_contact: SquarePen,
  create_interaction: MessageSquare,
  create_reminder: Calendar,
  get_contact_details: User,
  list_upcoming_reminders: Calendar,
}

// ── Tool Call Card ─────────────────────────────────────────

function ToolCallCard({
  tool,
  onSendMessage,
}: {
  tool: ToolCallEvent
  onSendMessage?: (text: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const Icon = TOOL_ICONS[tool.name] || Wrench
  const displayName = tool.name.replace(/_/g, ' ')

  return (
    <div className="bg-surface-container-low my-1 rounded-xl text-sm">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
      >
        <Icon className="text-on-surface-variant size-3.5 shrink-0" />
        <span className="text-on-surface-variant flex-1 truncate capitalize">{displayName}</span>
        {tool.result === undefined ? (
          <Loader2 className="text-on-surface-variant size-3.5 animate-spin" />
        ) : expanded ? (
          <ChevronUp className="text-on-surface-variant size-3.5" />
        ) : (
          <ChevronDown className="text-on-surface-variant size-3.5" />
        )}
      </button>
      <ChatArtifact tool={tool} onSendMessage={onSendMessage} />
      {expanded && (
        <div className="border-outline-variant/20 border-t px-3 py-2 text-xs">
          <p className="text-on-surface-variant mb-1 font-medium">Input:</p>
          <pre className="text-on-surface-variant mb-2 overflow-x-auto whitespace-pre-wrap">
            {JSON.stringify(tool.arguments, null, 2)}
          </pre>
          {tool.result !== undefined && (
            <>
              <p className="text-on-surface-variant mb-1 font-medium">Result:</p>
              <pre className="text-on-surface-variant overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(tool.result, null, 2)}
              </pre>
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ── Message Bubble ─────────────────────────────────────────

function MessageBubble({
  message,
  isStreaming,
  onSendMessage,
}: {
  message: ChatMessage
  isStreaming?: boolean
  onSendMessage?: (text: string) => void
}) {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="bg-primary text-primary-foreground max-w-[85%] rounded-2xl rounded-br-md px-4 py-2.5 text-sm">
          {message.content}
        </div>
      </div>
    )
  }

  if (message.role === 'error') {
    return (
      <div className="flex items-start gap-2">
        <AlertCircle className="text-destructive mt-0.5 size-4 shrink-0" />
        <div className="text-destructive text-sm">{message.content}</div>
      </div>
    )
  }

  // assistant
  return (
    <div className="flex items-start gap-2">
      <div className="bg-surface-container-high mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full">
        <Bot className="text-on-surface-variant size-3.5" />
      </div>
      <div className="max-w-[85%] min-w-0 space-y-1 overflow-hidden">
        {message.toolCalls.map((tc) => (
          <ToolCallCard key={tc.id} tool={tc} onSendMessage={onSendMessage} />
        ))}
        {message.content ? (
          <div className="chat-markdown bg-surface-container-high rounded-2xl rounded-bl-md px-4 py-2.5 text-sm">
            <Markdown remarkPlugins={[remarkGfm]}>{message.content}</Markdown>
            {isStreaming && (
              <span className="bg-foreground ml-0.5 inline-block h-4 w-1 animate-pulse align-text-bottom" />
            )}
          </div>
        ) : isStreaming ? (
          <div className="bg-surface-container-high rounded-2xl rounded-bl-md px-4 py-3 text-sm">
            <div className="flex items-center gap-1.5" aria-label="Aime is thinking…">
              <span className="typing-dot" />
              <span className="typing-dot" />
              <span className="typing-dot" />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

// ── Main Panel ─────────────────────────────────────────────

export function ChatPanel({ open, onClose, voiceStart, initialInput }: ChatPanelProps) {
  const { messages, status, send, stop, newSession, retry } = useChat()
  const [input, setInput] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages])

  // Focus input when panel opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [open])

  // Pre-fill input from voice overlay transcription — intentional cascade for immediate UX feedback
  useEffect(() => {
    if (open && initialInput) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setInput(initialInput)
      setTimeout(() => inputRef.current?.focus(), 150)
    }
  }, [open, initialInput])

  const handleSubmit = (e?: FormEvent) => {
    e?.preventDefault()
    const text = input.trim()
    if (!text || status === 'streaming') return
    setInput('')
    send(text)
  }

  const handleSuggestion = (label: string) => {
    send(label)
  }

  const handleVoiceTranscribed = (text: string) => {
    setInput((prev) => (prev ? prev + ' ' + text : text))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  if (!open) return null

  const isEmpty = messages.length === 0
  const lastMessage = messages[messages.length - 1]
  const showRetry = status === 'error' || lastMessage?.role === 'error'

  return (
    <div
      className="bg-background fixed inset-0 z-50 flex flex-col"
      style={{ paddingTop: 'var(--safe-area-top)', paddingBottom: 'var(--safe-area-bottom)' }}
    >
      {/* Header */}
      <div className="border-outline-variant/20 flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <Sparkles className="text-primary size-5" />
          <h2 className="text-lg font-semibold">Aime</h2>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <button
              type="button"
              onClick={newSession}
              className="text-on-surface-variant hover:bg-surface-container-high hover:text-foreground flex size-9 items-center justify-center rounded-full"
              aria-label="New chat"
            >
              <SquarePen className="size-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="text-on-surface-variant hover:bg-surface-container-high hover:text-foreground flex size-9 items-center justify-center rounded-full"
            aria-label="Close chat"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>

      {/* Messages area */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4">
        {isEmpty ? (
          <div className="flex h-full flex-col items-center justify-center gap-6">
            <div className="bg-primary/10 flex size-16 items-center justify-center rounded-full">
              <Sparkles className="text-primary size-8" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold">Hi! I'm Aime</h3>
              <p className="text-on-surface-variant mt-1 text-sm">
                Your personal relationship assistant. How can I help?
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              {SUGGESTIONS.map(({ label, icon: SIcon }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => handleSuggestion(label)}
                  className="bg-surface-container-high hover:bg-surface-bright flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm transition-colors"
                >
                  <SIcon className="text-on-surface-variant size-3.5" />
                  {label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((msg, i) => (
              <MessageBubble
                key={i}
                message={msg}
                isStreaming={
                  status === 'streaming' && i === messages.length - 1 && msg.role === 'assistant'
                }
                onSendMessage={send}
              />
            ))}
          </div>
        )}
      </div>

      {/* Retry / Stop bar */}
      {(showRetry || status === 'streaming') && (
        <div className="border-outline-variant/20 flex justify-center border-t px-4 py-2">
          {status === 'streaming' ? (
            <button
              type="button"
              onClick={stop}
              className="text-on-surface-variant hover:bg-surface-container-high flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs"
            >
              Stop generating
            </button>
          ) : showRetry ? (
            <button
              type="button"
              onClick={retry}
              className="text-on-surface-variant hover:bg-surface-container-high flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs"
            >
              <RotateCcw className="size-3" />
              Retry
            </button>
          ) : null}
        </div>
      )}

      {/* Input bar */}
      <form onSubmit={handleSubmit} className="border-outline-variant/20 border-t px-4 py-3">
        <div className="border-outline-variant/30 bg-surface-container flex items-end gap-2 rounded-xl border px-3 py-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask Aime anything..."
            rows={1}
            className="placeholder:text-on-surface-variant max-h-32 flex-1 resize-none bg-transparent text-sm outline-none"
            style={{ minHeight: '36px' }}
          />
          <VoiceChatInput
            onTranscribed={handleVoiceTranscribed}
            disabled={status === 'streaming'}
            autoStart={voiceStart}
          />
          <button
            type="submit"
            disabled={!input.trim() || status === 'streaming'}
            className={cn(
              'flex size-9 shrink-0 items-center justify-center rounded-full transition-colors',
              input.trim() && status !== 'streaming'
                ? 'bg-primary text-primary-foreground'
                : 'text-on-surface-variant',
            )}
            aria-label="Send message"
          >
            <Send className="size-4" />
          </button>
        </div>
      </form>
    </div>
  )
}
